import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

const AddPayrollStructureDialog = ({
  open,
  onClose,
  onSubmit,
  structure,
  editMode,
  handleDelete,
  jobs,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    code: "",
    name: "",
    jobId: "",
    countryCode: "EG",
    currency: "EGP",
    active: true,
  });

  useEffect(() => {
    if (open) {
      setForm(
        structure
          ? { ...structure }
          : { code: "", name: "", jobId: "", countryCode: "EG", currency: "EGP", active: true }
      );
    }
  }, [open, structure]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {editMode
          ? t("admin.payroll.structures.editTitle")
          : t("admin.payroll.structures.addTitle")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("admin.payroll.structures.fields.code")}
            value={form.code}
            onChange={handleChange("code")}
            fullWidth
            required
          />
          <TextField
            label={t("admin.payroll.structures.fields.name")}
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            required
          />
          <Autocomplete
            options={jobs}
            getOptionLabel={(job) => job.jobName || ""}
            isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
            value={jobs.find((job) => job.jobId === form.jobId) || null}
            onChange={(event, newValue) =>
              setForm((prev) => ({ ...prev, jobId: newValue?.jobId || "" }))
            }
            renderInput={(params) => (
              <TextField {...params} label={t("admin.payroll.structures.fields.job")} />
            )}
          />
          <TextField
            label={t("admin.payroll.structures.fields.countryCode")}
            value={form.countryCode}
            onChange={handleChange("countryCode")}
            fullWidth
            required
          />
          <TextField
            label={t("admin.payroll.structures.fields.currency")}
            value={form.currency}
            onChange={handleChange("currency")}
            fullWidth
            required
          />
          <FormControlLabel
            control={<Switch checked={!!form.active} onChange={handleChange("active")} />}
            label={t("admin.payroll.structures.fields.active")}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={onClose} color="secondary">
          {t("common.cancel")}
        </MDButton>
        {editMode && (
          <MDButton onClick={handleDelete} color="error">
            {t("common.delete")}
          </MDButton>
        )}
        <MDButton
          onClick={() => onSubmit(form)}
          color="primary"
          disabled={
            !form.code?.trim() ||
            !form.name?.trim() ||
            !form.countryCode?.trim() ||
            !form.currency?.trim()
          }
        >
          {editMode ? t("common.update") : t("common.add")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

AddPayrollStructureDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  structure: PropTypes.object,
  editMode: PropTypes.bool,
  handleDelete: PropTypes.func,
  jobs: PropTypes.array,
};

AddPayrollStructureDialog.defaultProps = {
  jobs: [],
};

export default AddPayrollStructureDialog;
